import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  ListTable,
  ListTableThemeProvider
} from '~/modules/common/components/ListTable';
import { ResourceUserAvailabilityGroupType } from '~/types';
import { OVERVIEW_UNITS_ENUM } from '~/modules/resource-management/common/enums';
import { PROBLEM_FILTER_OPTIONS } from '../ProblemFilterDropdown';
import { useListTableColumns, useListTableFooterColumns } from './hooks';

export const useListTableStyles = makeStyles(() => ({
  card: {
    overflowX: 'auto',
    boxShadow: 'none'
  }
}));

export const useListTableThemeProviderStyles = makeStyles(theme => ({
  tableRoot: {
    width: '100%',
    borderCollapse: 'separate',
    '& td:last-child, th:last-child': {
      paddingRight: theme.spacing(2)
    },
    '& td:first-child, th:first-child': {
      borderLeft: 0,
      textAlign: 'left',
      paddingLeft: theme.spacing(2)
    },
    '& tfoot > tr > td:first-child': {
      textAlign: 'left',
      paddingRight: theme.spacing(2)
    },
    '& tfoot > tr > td': {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightBold
    }
  },
  tableCell: {
    height: theme.spacing(6),
    padding: theme.spacing(1.5, 1),
    textAlign: 'left',
    fontSize: theme.typography.body2.fontSize,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.caption.fontSize,
      whiteSpace: 'initial'
    }
  },
  tableHeaderCell: {
    height: theme.spacing(6),
    padding: theme.spacing(1.5, 1),
    textAlign: 'left',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.caption.fontSize,
      whiteSpace: 'initial'
    }
  },
  tableFooter: {
    backgroundColor: theme.palette.common.white
  },
  tableFooterCell: {
    height: theme.spacing(6),
    padding: theme.spacing(1.5, 1),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body2.fontSize,
    borderBottom: 'unset',
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.caption.fontSize,
      whiteSpace: 'initial'
    }
  }
}));

export const BenchOverAllocationTable = ({
  groupBy,
  rows,
  totals,
  selectedProblem,
  loading,
  hasMoreRows,
  overviewUnit
}) => {
  const providerClasses = useListTableThemeProviderStyles();
  const listTableClasses = useListTableStyles();

  const { listColumns, headers } = useListTableColumns({
    groupBy,
    selectedProblem,
    overviewUnit
  });
  const footers = useListTableFooterColumns({
    groupBy,
    totals,
    selectedProblem,
    overviewUnit
  });

  return (
    <ListTableThemeProvider classes={providerClasses}>
      <ListTable
        classes={listTableClasses}
        headers={headers}
        columns={listColumns}
        records={rows}
        footers={footers}
        totals={footers}
        variant="table"
        isLoading={loading}
        hasMore={hasMoreRows}
        enableA11ySupport
      />
    </ListTableThemeProvider>
  );
};

BenchOverAllocationTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  totals: PropTypes.object,
  groupBy: PropTypes.oneOf([
    ResourceUserAvailabilityGroupType.Role,
    ResourceUserAvailabilityGroupType.Location,
    ResourceUserAvailabilityGroupType.Division,
    ResourceUserAvailabilityGroupType.ServiceCenter,
    ResourceUserAvailabilityGroupType.CostCenter,
    ResourceUserAvailabilityGroupType.Department,
    ResourceUserAvailabilityGroupType.EmployeeType
  ]).isRequired,
  selectedProblem: PropTypes.oneOf([
    PROBLEM_FILTER_OPTIONS.BENCH,
    PROBLEM_FILTER_OPTIONS.OVER_ALLOCATION
  ]).isRequired,
  loading: PropTypes.bool,
  hasMoreRows: PropTypes.bool,
  overviewUnit: PropTypes.oneOf([
    OVERVIEW_UNITS_ENUM.HOURS,
    OVERVIEW_UNITS_ENUM.PERCENTAGE,
    OVERVIEW_UNITS_ENUM.FTE
  ]).isRequired
};

export default BenchOverAllocationTable;
